import React, { useEffect, useState } from 'react'
import { Button, Card, Col, Form, Input, List, Select, Space, Typography, InputNumber } from 'antd'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faPlus, faTrash } from '@fortawesome/free-solid-svg-icons'
import CustomRow from '../../../reusable-components/CustomRow'
import { useStressManagement } from '../hooks/useStressManagement'

const { Title, Text } = Typography
const { Option } = Select

const StressManagement = ({ stressManagement, setStressManagement }) => {
  const [form] = Form.useForm()
  const [selectedTechnique, setSelectedTechnique] = useState(null)
  const { stressManagementOptions, isLoading } = useStressManagement()

  useEffect(() => {
    form.resetFields()
  }, [selectedTechnique, form])

  const handleAddTechnique = (values) => {
    const newTechnique = {
      id: Date.now(),
      name: values.name,
      type: values.type,
      description: values.description,
      duration: values.duration,
      frequency: values.frequency,
      benefits: values.benefits,
      resources: values.resources
    }

    setStressManagement([...stressManagement, newTechnique])
    form.resetFields()
  }

  const handleDeleteTechnique = (id) => {
    setStressManagement(stressManagement.filter((item) => item.id !== id))
    if (selectedTechnique && selectedTechnique.id === id) {
      setSelectedTechnique(null)
    }
  }

  const handleEditTechnique = () => {
    if (!selectedTechnique) return

    const values = form.getFieldsValue()
    const updatedTechniques = stressManagement.map((item) => {
      if (item.id === selectedTechnique.id) {
        return {
          ...item,
          name: values.name,
          type: values.type,
          description: values.description,
          duration: values.duration,
          frequency: values.frequency,
          benefits: values.benefits,
          resources: values.resources
        }
      }
      return item
    })

    setStressManagement(updatedTechniques)
    setSelectedTechnique(null)
  }

  return (
    <CustomRow>
      <Col span={24}>
        <Title level={4}>Stress & Mental Wellness</Title>
        <Text>Configure stress management techniques and mental wellness practices for the wellness plan.</Text>
      </Col>

      <Col span={12} style={{ paddingRight: 12 }}>
        <Card title='Stress Management Techniques' loading={isLoading}>
          <List
            dataSource={stressManagement}
            renderItem={(item) => (
              <List.Item
                key={item.id}
                actions={[
                  <Button
                    type='text'
                    danger
                    onClick={() => handleDeleteTechnique(item.id)}
                    icon={<FontAwesomeIcon icon={faTrash} />}
                  />
                ]}
              >
                <List.Item.Meta
                  title={item.name}
                  description={`${item.type} - ${item.frequency}`}
                  style={{ cursor: 'pointer' }}
                  onClick={() => setSelectedTechnique(item)}
                />
              </List.Item>
            )}
            footer={
              <Button
                type='dashed'
                block
                onClick={() => setSelectedTechnique(null)}
                icon={<FontAwesomeIcon icon={faPlus} />}
              >
                Add New Stress Management Technique
              </Button>
            }
          />
        </Card>
      </Col>

      <Col span={12}>
        <Card title={selectedTechnique ? 'Edit Stress Management Technique' : 'Add Stress Management Technique'}>
          <Form
            form={form}
            layout='vertical'
            onFinish={selectedTechnique ? handleEditTechnique : handleAddTechnique}
            initialValues={selectedTechnique || {}}
          >
            <Form.Item name='name' label='Technique Name' rules={[{ required: true, message: 'Please enter a name' }]}>
              <Input placeholder='e.g., Mindful Breathing' />
            </Form.Item>

            <Form.Item
              name='type'
              label='Technique Type'
              rules={[{ required: true, message: 'Please select a technique type' }]}
            >
              <Select placeholder='Select technique type'>
                {stressManagementOptions.map((option) => (
                  <Option key={option.value} value={option.value}>
                    {option.label}
                  </Option>
                ))}
              </Select>
            </Form.Item>

            <Form.Item name='description' label='Description'>
              <Input.TextArea rows={3} placeholder='Describe the stress management technique...' />
            </Form.Item>

            <Form.Item
              name='duration'
              label='Duration (minutes)'
              rules={[{ required: true, message: 'Please specify duration' }]}
            >
              <InputNumber min={1} max={120} />
            </Form.Item>

            <Form.Item
              name='frequency'
              label='Frequency'
              rules={[{ required: true, message: 'Please select a frequency' }]}
            >
              <Select placeholder='Select frequency'>
                <Option value='daily'>Daily</Option>
                <Option value='twice daily'>Twice Daily</Option>
                <Option value='2-3 times per week'>2-3 times per week</Option>
                <Option value='weekly'>Weekly</Option>
                <Option value='as needed'>As Needed</Option>
              </Select>
            </Form.Item>

            <Form.Item name='benefits' label='Benefits'>
              <Select mode='multiple' placeholder='Select benefits'>
                <Option value='reduced-anxiety'>Reduced Anxiety</Option>
                <Option value='improved-focus'>Improved Focus</Option>
                <Option value='better-sleep'>Better Sleep</Option>
                <Option value='emotional-regulation'>Emotional Regulation</Option>
                <Option value='decreased-stress'>Decreased Stress</Option>
                <Option value='increased-mindfulness'>Increased Mindfulness</Option>
                <Option value='improved-resilience'>Improved Resilience</Option>
              </Select>
            </Form.Item>

            <Form.Item name='resources' label='Resources'>
              <Select mode='multiple' placeholder='Select resources'>
                <Option value='guided-audio'>Guided Audio</Option>
                <Option value='written-instructions'>Written Instructions</Option>
                <Option value='video-tutorial'>Video Tutorial</Option>
                <Option value='mobile-app'>Mobile App</Option>
                <Option value='workshop'>Workshop</Option>
                <Option value='one-on-one-session'>One-on-One Session</Option>
              </Select>
            </Form.Item>

            <Form.Item>
              <Space>
                <Button type='primary' htmlType='submit'>
                  {selectedTechnique ? 'Update' : 'Add'} Technique
                </Button>
                {selectedTechnique && <Button onClick={() => setSelectedTechnique(null)}>Cancel</Button>}
              </Space>
            </Form.Item>
          </Form>
        </Card>
      </Col>
    </CustomRow>
  )
}

export default StressManagement
